import React, { useEffect, useState, useCallback } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../services/apiClient';
import './OnboardingChecklist.scss';

interface OnboardingTask {
  key: string;
  label: string;
  order: number;
  completed: boolean;
  completedAt: string | null;
}

interface OnboardingData {
  tasks: OnboardingTask[];
  completedCount: number;
  totalCount: number;
  allCompleted: boolean;
  progress: number;
}

export const OnboardingChecklist: React.FC = () => {
  const [data, setData] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('onboarding_collapsed') === 'true';
  });

  const fetchProgress = useCallback(async () => {
    try {
      const response = await api.get('/api/onboarding');
      setData(response.data);
    } catch (error) {
      console.error('[Onboarding] Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('onboarding_collapsed', String(newState));
  };

  if (loading || !data || data.allCompleted) {
    return null;
  }

  const progressPercent = data.progress;
  const circumference = 2 * Math.PI * 20;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className={`onboarding-checklist ${collapsed ? 'collapsed' : ''}`}>
      <div className="onboarding-header">
        <div className="header-left">
          <span className="onboarding-emoji">✌️</span>
          <span className="onboarding-title">Almost There...</span>
        </div>
        <button className="collapse-btn" onClick={toggleCollapsed} aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      
      {!collapsed && (
        <div className="onboarding-content">
          <div className="onboarding-tasks">
            {data.tasks.map((task) => (
              <div 
                key={task.key} 
                className={`onboarding-task ${task.completed ? 'completed' : ''}`}
              >
                <div className={`task-checkbox ${task.completed ? 'checked' : ''}`}>
                  {task.completed && <Check size={14} strokeWidth={3} />}
                </div>
                <span className={`task-label ${task.completed ? 'strikethrough' : ''}`}>
                  {task.label}
                </span>
              </div>
            ))}
          </div>
          
          <div className="onboarding-progress">
            <svg className="progress-ring" width="48" height="48">
              <circle
                className="progress-ring-bg"
                cx="24"
                cy="24"
                r="20"
                fill="none"
                strokeWidth="4"
              />
              <circle
                className="progress-ring-fill"
                cx="24"
                cy="24"
                r="20"
                fill="none"
                strokeWidth="4"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
